import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";

import { crmApi } from "../lib/crm-api";
import { queryKeys } from "../lib/queryKeys";
import { ApiError } from "../lib/api";
import {
  TIMELINE_FILTERS,
  TimelineEntryRow,
} from "./crm/TimelineEntryRow";
import { formatPhone } from "./crm/PhoneField";
import { Button } from "./ui/Button";
import { cn } from "./ui/cn";

/**
 * A company's or a person's history, over the Helpdesk.
 *
 * Agents recognise accounts, not ticket ids: "what else is going on with Acme"
 * is the question asked halfway through reading their email, and until now the
 * only answer was to leave the queue for the CRM and find the way back
 * afterwards. So the record comes to them instead -- the same merged timeline
 * the company page shows, in a panel over the list.
 *
 * A drawer rather than a route. Triage is a sequence of small lookups, and one
 * that costs a navigation is one nobody does; this closes on Escape and leaves
 * the ticket underneath exactly as it was. The company drawer links out to the
 * full CRM record for anything beyond reading -- editing an account is the
 * CRM's job, and duplicating it here is how two screens start to disagree.
 */

export type ActivityTarget =
  | { kind: "company"; id: number; name: string }
  | { kind: "contact"; id: number; name: string };

interface ActivityDrawerApi {
  open: (target: ActivityTarget) => void;
}

const ActivityDrawerContext = createContext<ActivityDrawerApi | null>(null);

/**
 * How anything under the Helpdesk shell opens the drawer.
 *
 * Returns null outside the provider rather than throwing, so a component that
 * happens to render in both products -- or in a test -- degrades to plain text
 * instead of crashing the screen it is on.
 */
export function useActivityDrawer(): ActivityDrawerApi | null {
  return useContext(ActivityDrawerContext);
}

/**
 * A company or contact name that opens its history.
 *
 * Degrades to plain text -- same size, same colour, no affordance -- when there
 * is nothing to open: a ticket ingest could not attribute has no contact row,
 * and a dead link that looks live is worse than a name that was never clickable.
 *
 * Clicks are stopped from propagating because the commonest place this renders
 * is a ticket row, where the whole row opens the ticket. Reading who somebody
 * is should not also change which conversation is on screen.
 */
export function ActivityLink({
  kind,
  id,
  name,
  className,
  children,
}: {
  kind: ActivityTarget["kind"];
  id: number | null;
  name: string | null;
  className?: string;
  /** Defaults to the name; pass this when the label differs from the record. */
  children?: React.ReactNode;
}) {
  const drawer = useActivityDrawer();
  const label = children ?? name;

  if (!drawer || id === null || name === null) return <>{label}</>;

  return (
    <button
      type="button"
      title={
        kind === "company"
          ? `Everything that has happened with ${name}`
          : `Everything ${name} has been part of`
      }
      onClick={(event) => {
        event.stopPropagation();
        drawer.open({ kind, id, name });
      }}
      className={cn(
        "max-w-full truncate text-left hover:text-brand-600 hover:underline",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
        className,
      )}
    >
      {label}
    </button>
  );
}

export function ActivityDrawerProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [target, setTarget] = useState<ActivityTarget | null>(null);

  /*
   * Stable, so every clickable name in a fifty-row list is not re-rendered
   * each time the drawer opens or closes.
   */
  const api = useMemo<ActivityDrawerApi>(
    () => ({ open: (next: ActivityTarget) => setTarget(next) }),
    [],
  );

  const close = useCallback(() => setTarget(null), []);

  // Escape closes it, matching every other overlay in the app.
  useEffect(() => {
    if (!target) return;

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [target, close]);

  return (
    <ActivityDrawerContext.Provider value={api}>
      {children}
      {target ? (
        <ActivityDrawer target={target} onClose={close} onOpen={api.open} />
      ) : null}
    </ActivityDrawerContext.Provider>
  );
}

function ActivityDrawer({
  target,
  onClose,
  onOpen,
}: {
  target: ActivityTarget;
  onClose: () => void;
  /** So the contact header can hand over to their company's drawer. */
  onOpen: (target: ActivityTarget) => void;
}) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");

  /*
   * Reset when the drawer is pointed at somebody else. Carrying "Calls" over
   * from the last person onto a record that has none reads as an empty history
   * rather than a filtered one.
   */
  useEffect(() => setFilter("all"), [target.kind, target.id]);

  const kinds =
    TIMELINE_FILTERS.find((entry) => entry.id === filter)?.kinds ?? [];

  const timeline = useInfiniteQuery({
    queryKey:
      target.kind === "company"
        ? queryKeys.crmTimeline(target.id, kinds, false)
        : queryKeys.crmContactTimeline(target.id, kinds),
    queryFn: ({ pageParam }) =>
      target.kind === "company"
        ? crmApi.timeline(target.id, {
            kinds,
            cursor: pageParam as string | null,
          })
        : crmApi.contactTimeline(target.id, {
            kinds,
            cursor: pageParam as string | null,
          }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: 15_000,
  });

  const entries = timeline.data?.pages.flatMap((page) => page.entries) ?? [];

  /*
   * Opening a ticket closes the drawer. It navigates the list underneath, and
   * leaving a panel about Acme floating over a ticket the reader just chose
   * from it is a stale screen a moment later.
   */
  const openTicket = (ticketId: number): void => {
    onClose();
    navigate(`/v/open/${ticketId}`);
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      {/*
        A button so dismissing works by click and by keyboard, and so it is
        labelled rather than being an unexplained dark area to a screen reader.
      */}
      <button
        type="button"
        aria-label="Close activity"
        onClick={onClose}
        className="h-full flex-1 bg-black/30"
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`Activity for ${target.name}`}
        className="flex h-full w-full max-w-md flex-col border-l border-line bg-surface shadow-xl"
      >
        {target.kind === "company" ? (
          <CompanyHeader id={target.id} name={target.name} onClose={onClose} />
        ) : (
          <ContactHeader
            id={target.id}
            name={target.name}
            onClose={onClose}
            onOpen={onOpen}
          />
        )}

        <div className="flex flex-wrap gap-1 border-b border-line px-4 py-2">
          {TIMELINE_FILTERS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              onClick={() => setFilter(entry.id)}
              className={cn(
                "rounded-full px-2.5 py-1 text-xs transition-colors",
                filter === entry.id
                  ? "bg-brand-600 text-white"
                  : "text-muted hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400",
              )}
            >
              {entry.label}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {timeline.isLoading ? (
            <p className="py-8 text-center text-xs text-faint">Loading…</p>
          ) : timeline.isError ? (
            <p className="py-8 text-center text-xs text-sla-breached">
              Could not load this history.
            </p>
          ) : entries.length === 0 ? (
            <p className="py-8 text-center text-xs text-faint">
              {filter === "all"
                ? "Nothing recorded yet."
                : "Nothing of that kind yet."}
            </p>
          ) : (
            /* Same grid as the CRM's timeline -- the rows are the same
               component and size their badge and time columns from this. */
            <ol className="grid grid-cols-[max-content_1fr_max-content] gap-3">
              {entries.map((entry) => (
                <TimelineEntryRow
                  key={entry.id}
                  entry={entry}
                  onOpenTicket={openTicket}
                />
              ))}
            </ol>
          )}

          {timeline.hasNextPage ? (
            <div className="py-4 text-center">
              <Button
                size="sm"
                variant="secondary"
                disabled={timeline.isFetchingNextPage}
                onClick={() => void timeline.fetchNextPage()}
              >
                {timeline.isFetchingNextPage ? "Loading…" : "Older"}
              </Button>
            </div>
          ) : null}
        </div>
      </aside>
    </div>
  );
}

/**
 * The company's name, who manages it, and the way through to the full record.
 *
 * The detail query is the same one the CRM's company page uses, so arriving
 * here and then following the link through is one request rather than two.
 */
function CompanyHeader({
  id,
  name,
  onClose,
}: {
  id: number;
  name: string;
  onClose: () => void;
}) {
  const company = useQuery({
    queryKey: queryKeys.crmCompany(id),
    queryFn: () => crmApi.company(id),
    staleTime: 15_000,
  });

  const detail = company.data;

  return (
    <DrawerHeader
      /* The name off the ticket until the record answers, so the panel is
         never briefly titled "Loading". */
      title={detail?.name ?? name}
      onClose={onClose}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
        {detail ? (
          detail.owner ? (
            <span className="text-muted">
              Managed by{" "}
              <span className="text-content">{detail.owner.name}</span>
            </span>
          ) : (
            <span className="text-sla-warning">No account manager</span>
          )
        ) : null}

        {detail?.tradeAccountNumber ? (
          <span className="text-faint">
            Account {detail.tradeAccountNumber}
          </span>
        ) : null}

        <Link
          to={`/crm/companies/${id}`}
          onClick={onClose}
          className="ml-auto text-brand-600 hover:text-brand-700"
        >
          Open in CRM ↗
        </Link>
      </div>
    </DrawerHeader>
  );
}

/**
 * The person, and the account they sit under.
 *
 * Their company is a button rather than a link out: "who else have we spoken
 * to there" follows "what have we said to them" often enough that it should not
 * cost leaving the Helpdesk.
 */
function ContactHeader({
  id,
  name,
  onClose,
  onOpen,
}: {
  id: number;
  name: string;
  onClose: () => void;
  onOpen: (target: ActivityTarget) => void;
}) {
  const contact = useQuery({
    queryKey: queryKeys.crmContact(id),
    queryFn: () => crmApi.contact(id),
    staleTime: 60_000,
  });

  const detail = contact.data;
  const missing =
    contact.error instanceof ApiError && contact.error.status === 404;

  return (
    <DrawerHeader title={detail?.name ?? detail?.email ?? name} onClose={onClose}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
        {detail?.role ? <span className="text-muted">{detail.role}</span> : null}

        {detail?.email ? (
          <a
            href={`mailto:${detail.email}`}
            className="text-muted hover:text-brand-600"
          >
            {detail.email}
          </a>
        ) : null}

        {detail?.phone ? (
          /* E.164 to dial, grouped to read -- same as the CRM's contact rows. */
          <a
            href={`tel:${detail.phone}`}
            className="text-muted hover:text-brand-600"
          >
            {formatPhone(detail.phone)}
          </a>
        ) : null}

        {detail?.company ? (
          <button
            type="button"
            onClick={() =>
              onOpen({
                kind: "company",
                id: detail.company!.id,
                name: detail.company!.name,
              })
            }
            className="text-brand-600 hover:text-brand-700"
          >
            {detail.company.name} ↗
          </button>
        ) : detail ? (
          /* A real state, not an error: ingest records everybody who writes in,
             and filing them is a queue somebody works through. */
          <span
            title="Nobody has filed this person under a company yet"
            className="text-sla-warning"
          >
            Not filed under a company
          </span>
        ) : null}

        {missing ? (
          <span className="text-sla-breached">
            This person is no longer in the CRM.
          </span>
        ) : null}
      </div>
    </DrawerHeader>
  );
}

function DrawerHeader({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <header className="border-b border-line bg-surface-2 px-4 py-3">
      <div className="flex items-start gap-2">
        <h2 className="min-w-0 flex-1 truncate text-sm font-semibold text-content">
          {title}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close activity"
          className={cn(
            "-mr-1 shrink-0 rounded px-1 text-sm text-faint",
            "hover:text-brand-600 dark:hover:text-brand-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
          )}
        >
          ✕
        </button>
      </div>
      <div className="mt-1">{children}</div>
    </header>
  );
}
